import React, { useEffect, useState } from "react";
import { offmarketApi } from "../../lib/api";
import { toast } from "sonner";
import { Save, Target } from "lucide-react";
import { MoneyInput } from "../shared";

const N = (v) => (v === "" || v == null ? null : Number(v));

// Bolt-on fit rules used by the Claude scorer. Saved per deal.
export const CriteriaPanel = ({ dealKey, defaults, onSaved }) => {
  const [c, setC] = useState({ niche: "", homeBase: "", radiusMiles: 100, revenueMin: "", revenueMax: "", employeesMin: "", employeesMax: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    offmarketApi.criteria(dealKey).then((r) => {
      const merged = { ...defaults, ...Object.fromEntries(Object.entries(r || {}).filter(([, v]) => v !== null && v !== "" && v !== undefined)) };
      setC((prev) => ({ ...prev, ...merged, revenueMin: merged.revenueMin ?? "", revenueMax: merged.revenueMax ?? "", employeesMin: merged.employeesMin ?? "", employeesMax: merged.employeesMax ?? "" }));
      onSaved?.(merged);
    }).catch(() => setC((prev) => ({ ...prev, ...defaults })));
  }, [dealKey]); // eslint-disable-line

  const set = (k, v) => { setC((s) => ({ ...s, [k]: v })); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try {
      const payload = { dealKey, niche: c.niche, homeBase: c.homeBase, radiusMiles: Number(c.radiusMiles) || 100, revenueMin: N(c.revenueMin), revenueMax: N(c.revenueMax), employeesMin: N(c.employeesMin), employeesMax: N(c.employeesMax), notes: c.notes };
      const r = await offmarketApi.saveCriteria(payload);
      onSaved?.(r); setDirty(false);
      toast.success("Bolt-on criteria saved — re-score targets to apply");
    } catch { toast.error("Could not save criteria"); }
    finally { setSaving(false); }
  };

  return (
    <div className="card om-crit" data-testid="om-criteria">
      <div className="om-crit-h">
        <div><Target size={13} /><b>Bolt-on fit rules</b><span>What a 100-point target looks like for this platform</span></div>
        <button className={`btn ${dirty ? "btn-primary" : ""}`} onClick={save} disabled={saving} data-testid="om-crit-save"><Save size={12} />{saving ? "Saving…" : dirty ? "Save rules" : "Saved"}</button>
      </div>
      <div className="om-crit-grid">
        <div className="field span2"><label>Niche / trade</label><input className="inp" value={c.niche} onChange={(e) => set("niche", e.target.value)} placeholder="Landscaping & grounds maintenance" data-testid="om-crit-niche" /></div>
        <div className="field span2"><label>Home base</label><input className="inp" value={c.homeBase} onChange={(e) => set("homeBase", e.target.value)} placeholder="McKinney, TX" data-testid="om-crit-home" /></div>
        <div className="field"><label>Radius (miles)</label><input className="inp mono" type="number" min={5} value={c.radiusMiles} onChange={(e) => set("radiusMiles", e.target.value)} data-testid="om-crit-radius" /></div>
        <div className="field"><label>Revenue min</label><MoneyInput value={c.revenueMin} onChange={(v) => set("revenueMin", v)} placeholder="500,000" testid="om-crit-revmin" /></div>
        <div className="field"><label>Revenue max</label><MoneyInput value={c.revenueMax} onChange={(v) => set("revenueMax", v)} placeholder="5,000,000" testid="om-crit-revmax" /></div>
        <div className="field"><label>Employees</label>
          <div className="om-range">
            <input className="inp mono" type="number" value={c.employeesMin} onChange={(e) => set("employeesMin", e.target.value)} placeholder="5" data-testid="om-crit-empmin" />
            <span>to</span>
            <input className="inp mono" type="number" value={c.employeesMax} onChange={(e) => set("employeesMax", e.target.value)} placeholder="60" data-testid="om-crit-empmax" />
          </div>
        </div>
        <div className="field span4"><label>Other must-haves</label><input className="inp" value={c.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Commercial maintenance contracts, owner 55+, no franchise…" data-testid="om-crit-notes" /></div>
      </div>
    </div>
  );
};
